import { useState } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';

export type IconButtonSize = 'sm' | 'md' | 'lg';

const sizes: Record<IconButtonSize, number> = { sm: 28, md: 32, lg: 36 };

export interface IconButtonProps {
    icon?: ReactNode;
    /** Nome acessível. Obrigatório: o ícone sozinho não nomeia o botão. */
    label: string;
    size?: IconButtonSize;
    variant?: 'ghost' | 'outline';
    active?: boolean;
    disabled?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    style?: CSSProperties;
}

export function IconButton({
    icon, label, size = 'md', variant = 'ghost', active = false, disabled = false, onClick, style,
}: IconButtonProps) {
    const [hover, setHover] = useState(false);
    const d = sizes[size] ?? sizes.md;
    const base: CSSProperties = variant === 'outline'
        ? { background: 'var(--surface)', border: '1px solid var(--border-strong)' }
        : { background: 'transparent', border: '1px solid transparent' };
    return (
        <button
            type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                width: d, height: d, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 'var(--radius-xs)', cursor: disabled ? 'not-allowed' : 'pointer',
                color: active ? 'var(--text-brand)' : 'var(--neutral-600)',
                transition: 'var(--transition-control)', opacity: disabled ? 0.45 : 1,
                ...base,
                background: active ? 'var(--surface-selected)' : (hover && !disabled ? 'var(--surface-hover)' : base.background),
                ...style,
            }}
        >
            {icon}
        </button>
    );
}
